import React, { useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { testWhatsAppConnection } from "../../ApiCalls/whatsappAccounts";

const WhatsAppAccountsForm = ({
  accounts,
  onChange,
  agentId,
  token,
  onBulkTemplate,
}) => {
  const [testingAccount, setTestingAccount] = useState(null);
  const [, setTestResult] = useState(null);
  const [showApiKeys, setShowApiKeys] = useState({});

  const addAccount = () => {
    const newAccount = {
      accountName: "",
      phoneId: "",
      apiKey: "",
      isActive: true,
      webhookUrl: "",
      responseMode: "REGISTERED_ONLY",
    };
    onChange([...accounts, newAccount]);
  };

  const updateAccount = (index, field, value) => {
    const updated = [...accounts];
    updated[index][field] = value;
    onChange(updated);
  };

  const removeAccount = (index) => {
    const updated = accounts.filter((_, i) => i !== index);
    onChange(updated);
  };

  const toggleApiKeyVisibility = (index) => {
    setShowApiKeys((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const testConnection = async (account, index) => {
    if (!token) {
      alert("Token de autenticación requerido para testing");
      return;
    }

    // If we're creating a new agent (no agentId), we need to test with temporary data
    if (!agentId) {
      if (!account.phoneId || !account.apiKey) {
        alert("Phone ID y API Key son requeridos para probar la conexión");
        return;
      }

      try {
        setTestingAccount(index);
        setTestResult(null);

        // Make direct API call to test connection
        const response = await fetch(
          `https://graph.facebook.com/v17.0/${account.phoneId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${account.apiKey}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setTestResult({
            success: true,
            message: "Conexión exitosa",
            data: data,
          });
          alert("Conexión exitosa");
        } else {
          const errorData = await response.json();
          setTestResult({
            success: false,
            error: errorData.error?.message || response.statusText,
          });
          alert(
            `Error en la conexión: ${
              errorData.error?.message || response.statusText
            }`
          );
        }
      } catch (error) {
        setTestResult({
          success: false,
          error: error.message,
        });
        alert("Error al probar la conexión");
      } finally {
        setTestingAccount(null);
      }
      return;
    }

    // If we have an agentId, use the API endpoint
    try {
      setTestingAccount(index);
      setTestResult(null);

      const result = await testWhatsAppConnection(
        token,
        agentId,
        account.phoneId
      );

      setTestResult({
        success: result.success,
        message: result.message,
        error: result.error,
      });

      if (result.success) {
        alert("Conexión exitosa");
      } else {
        alert(`Error en la conexión: ${result.error || result.message}`);
      }
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message,
      });
      alert("Error al probar la conexión");
    } finally {
      setTestingAccount(null);
    }
  };

  const generateWebhookUrl = (phoneId) => {
    if (!phoneId) return "";

    // Use backend URL for webhook generation
    const backendUrl =
      process.env.NODE_ENV === "production" &&
      navigator.userAgent !== "ReactSnap"
        ? process.env.REACT_APP_API_URL
        : "http://localhost:5001/api";

    return `${backendUrl}/whatsapp/webhook/${phoneId}`;
  };

  const handlePhoneIdChange = (index, phoneId) => {
    updateAccount(index, "phoneId", phoneId);
    updateAccount(index, "webhookUrl", generateWebhookUrl(phoneId));
  };

  return (
    <div>
      {accounts.map((account, index) => (
        <Card key={index} className="mb-4 shadow-sm">
          <CardBody className="p-4">
            {/* Account Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 text-primary">
                <i className="fab fa-whatsapp me-2 text-success"></i>
                Cuenta WhatsApp #{index + 1}
              </h6>
              <Button
                color="danger"
                size="sm"
                onClick={() => removeAccount(index)}
                className="d-flex align-items-center"
              >
                <i className="fas fa-trash me-1"></i>
                Eliminar
              </Button>
            </div>

            {/* Basic Account Information */}
            <Row className="mb-3">
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-semibold">Nombre de la Cuenta *</Label>
                  <Input
                    value={account.accountName}
                    onChange={(e) =>
                      updateAccount(index, "accountName", e.target.value)
                    }
                    placeholder="Mi Cuenta WhatsApp"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-semibold">Phone ID *</Label>
                  <Input
                    value={account.phoneId}
                    onChange={(e) => handlePhoneIdChange(index, e.target.value)}
                    placeholder="123456789012345"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-semibold">
                    API Key o Access Token *
                  </Label>
                  <div className="position-relative">
                    <Input
                      type={showApiKeys[index] ? "text" : "password"}
                      value={account.apiKey}
                      onChange={(e) =>
                        updateAccount(index, "apiKey", e.target.value)
                      }
                      placeholder="EAAxxxxxxxxxxxx"
                      required
                      style={{ paddingRight: "45px" }}
                    />
                    <Button
                      type="button"
                      color="link"
                      size="sm"
                      className="position-absolute top-50 end-0 translate-middle-y pe-2"
                      onClick={() => toggleApiKeyVisibility(index)}
                      style={{
                        border: "none",
                        background: "none",
                        padding: "0",
                        zIndex: 10,
                      }}
                    >
                      <i
                        className={`fas ${
                          showApiKeys[index] ? "fa-eye-slash" : "fa-eye"
                        } text-muted`}
                      ></i>
                    </Button>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            {/* Webhook URL Section */}
            <div className="webhook-section mb-3">
              <Label className="fw-bold mb-2">Webhook URL</Label>
              <Row>
                <Col md={12}>
                  <Input
                    value={
                      account.webhookUrl || generateWebhookUrl(account.phoneId)
                    }
                    onChange={(e) =>
                      updateAccount(index, "webhookUrl", e.target.value)
                    }
                    placeholder="https://your-domain.com/whatsapp/webhook/phoneId"
                    className="mb-2"
                  />
                  <small className="text-muted d-block mb-3">
                    URL del webhook para recibir mensajes de WhatsApp. Puedes
                    editarla para testing.
                  </small>
                </Col>
              </Row>

              {/* Webhook Action Buttons */}
              <Row>
                <Col md={12}>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => {
                        const defaultUrl = generateWebhookUrl(account.phoneId);
                        updateAccount(index, "webhookUrl", defaultUrl);
                      }}
                      disabled={!account.phoneId}
                      className="d-flex align-items-center"
                    >
                      <i className="fas fa-magic me-1"></i>
                      Generar URL
                    </Button>
                    <Button
                      color="info"
                      size="sm"
                      onClick={() => {
                        const urlToCopy =
                          account.webhookUrl ||
                          generateWebhookUrl(account.phoneId);
                        if (urlToCopy) {
                          navigator.clipboard.writeText(urlToCopy);
                          alert("Webhook URL copiada al portapapeles");
                        }
                      }}
                      disabled={!account.phoneId}
                      className="d-flex align-items-center"
                    >
                      <i className="fas fa-copy me-1"></i>
                      Copiar URL
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => testConnection(account, index)}
                      disabled={
                        testingAccount === index ||
                        !account.phoneId ||
                        !account.apiKey ||
                        !token
                      }
                      className="d-flex align-items-center"
                    >
                      {testingAccount === index ? (
                        <>
                          <Spinner size="sm" className="me-1" />
                          Probando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plug me-1"></i>
                          Probar Conexión
                        </>
                      )}
                    </Button>
                    {onBulkTemplate && (
                      <Button
                        color="warning"
                        size="sm"
                        onClick={() => onBulkTemplate(account)}
                        disabled={!account.phoneId || !account.apiKey}
                        className="d-flex align-items-center"
                      >
                        <i className="fas fa-paper-plane me-1"></i>
                        Envío Masivo
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Account Configuration */}
            <div className="mt-3 pt-3 border-top">
              <Row>
                <Col md={6}>
                  <div className="d-flex align-items-center">
                    <div
                      className={`toggle-switch ${
                        account.isActive ? "active" : ""
                      }`}
                      onClick={() =>
                        updateAccount(index, "isActive", !account.isActive)
                      }
                      style={{
                        width: "60px",
                        height: "30px",
                        backgroundColor: account.isActive
                          ? "#28a745"
                          : "#6c757d",
                        borderRadius: "15px",
                        position: "relative",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        border: "2px solid transparent",
                      }}
                    >
                      <div
                        className="toggle-slider"
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: "white",
                          borderRadius: "50%",
                          position: "absolute",
                          top: "1px",
                          left: account.isActive ? "33px" : "1px",
                          transition: "all 0.3s ease",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}
                      />
                    </div>
                    <span
                      className="ms-3 fw-semibold"
                      style={{
                        color: account.isActive ? "#28a745" : "#6c757d",
                      }}
                    >
                      Cuenta Activa
                    </span>
                  </div>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="fw-semibold">Modo de Respuesta</Label>
                    <Input
                      type="select"
                      value={account.responseMode || "REGISTERED_ONLY"}
                      onChange={(e) =>
                        updateAccount(index, "responseMode", e.target.value)
                      }
                    >
                      <option value="REGISTERED_ONLY">
                        Solo Usuarios Registrados
                      </option>
                      <option value="ANYONE">Cualquier Persona</option>
                    </Input>
                    <small className="text-muted">
                      {account.responseMode === "REGISTERED_ONLY"
                        ? "El agente solo responderá a usuarios registrados en Mantis Business"
                        : "El agente responderá a cualquier persona que envíe un mensaje"}
                    </small>
                  </FormGroup>
                </Col>
              </Row>
            </div>

            {/* Instructions */}
            <div className="mt-4 p-3 bg-light rounded border-start border-4 border-info">
              <h6 className="mb-3 text-info">
                <i className="fas fa-info-circle me-2"></i>
                Instrucciones para configurar WhatsApp Business API
              </h6>
              <ol className="mb-0 small text-muted">
                <li className="mb-1">
                  Ve a{" "}
                  <a
                    href="https://developers.facebook.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-info"
                  >
                    Facebook Developers
                  </a>
                </li>
                <li className="mb-1">
                  Crea una aplicación y configura WhatsApp Business API
                </li>
                <li className="mb-1">
                  Obtén el Phone ID y API Key desde tu aplicación
                </li>
                <li className="mb-1">
                  <strong>Para testing local:</strong> Usa ngrok para exponer tu
                  servidor local
                </li>
                <li className="mb-1">
                  Edita la URL del webhook si necesitas usar una URL diferente
                  (ej: ngrok)
                </li>
                <li className="mb-1">
                  Copia la URL del webhook y configúrala en Facebook Developer
                  Console
                </li>
                <li className="mb-1">
                  Verifica el webhook - Meta enviará un challenge que será
                  verificado automáticamente
                </li>
                <li className="mb-1">
                  Suscríbete a los eventos de mensajes (messages,
                  message_deliveries, message_reads)
                </li>
                <li className="mb-0">
                  Prueba la conexión usando el botón "Probar Conexión"
                </li>
              </ol>
              <div className="alert alert-info mt-2 mb-0">
                <strong>Tip para testing:</strong> Puedes editar la URL del
                webhook para usar tu URL de ngrok o cualquier otra URL de
                testing.
                {!agentId && (
                  <div className="mt-1">
                    <strong>Nota:</strong> Al crear un nuevo agente, la prueba
                    de conexión se realiza directamente con la API de WhatsApp.
                  </div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      ))}

      <Button color="outline-primary" onClick={addAccount}>
        <i className="fas fa-plus me-2"></i>
        Agregar Cuenta de WhatsApp
      </Button>
    </div>
  );
};

export default WhatsAppAccountsForm;
